import { computed, ref, watch } from 'vue';
import jwtDecode from 'jwt-decode';

import useBulkCvBatches from './useBulkCvBatches';
import store from '@/store';

const {
  activeBatches,
  initialized,
  selectedBatch,
  selectedBatchId,
  realtime,
  loadSettings,
  applyRealtimeBatchUpdate,
  refreshBatch,
  refreshActiveBatchOverviews,
  refreshBatches,
  resetState
} = useBulkCvBatches();

const connectionState = ref('polling');
const lastEventAt = ref(null);
const lastRefreshAt = ref(null);
const listeners = new Set();

let socket = null;
let pollTimer = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let consumers = 0;
let stopped = true;
let pollTicks = 0;
let socketFlushTimer = null;
let socketHeartbeatTimer = null;
let pollRefreshPromise = null;
let connectionRecoveryPromise = null;
let lastSocketMessageAt = 0;
let eventGeneration = 0;
const pendingSocketEvents = new Map();

const POLL_INTERVAL_MS = 12000;
const SOCKET_HEARTBEAT_MS = 20000;
const SOCKET_STALE_MS = 45000;

const tokenIdentity = (token) => {
  if (!token) return null;
  try {
    const claims = jwtDecode(token);
    return claims?.user_id ?? claims?.sub ?? claims?.id ?? null;
  } catch (_) {
    return null;
  }
};

const socketConfig = computed(() => {
  const scoped = realtime.authorized ? realtime : null;
  const batch = selectedBatch.value?.realtime;
  const candidate = scoped || (batch?.authorized === true ? batch : null);
  if (!candidate) return null;
  const websocketUrl = candidate.websocket_url || candidate.url;
  const subprotocol = candidate.subprotocol;
  // Authentication is deliberately carried in the WebSocket subprotocol, not
  // a query string where it could be written to proxy and browser logs.
  if (!websocketUrl || !subprotocol) return null;
  return {
    websocketUrl,
    subprotocol
  };
});

const notifyListeners = (event) => {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (_) {
      // A reporting view must not be able to stop the global live connection.
    }
  });
};

const clearSocketHeartbeat = () => {
  if (socketHeartbeatTimer) clearInterval(socketHeartbeatTimer);
  socketHeartbeatTimer = null;
  lastSocketMessageAt = 0;
};

const closeSocket = () => {
  clearSocketHeartbeat();
  if (socket) {
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
    try {
      socket.close();
    } catch (_) {
      // A CONNECTING browser socket can reject close during a rapid role/route
      // transition; references and handlers are still released below.
    }
  }
  socket = null;
};

const clearConnectionTimers = () => {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
};

const refreshAuthoritativeState = async (event = { source: 'poll' }) => {
  try {
    const reference =
      event?.payload?.batch_reference ||
      event?.payload?.reference ||
      event?.payload?.batch?.reference ||
      null;
    if (reference && String(reference) === String(selectedBatchId.value)) {
      if (!applyRealtimeBatchUpdate(event.payload)) {
        await refreshBatch(selectedBatchId.value, { quiet: true });
      } else if (event?.payload?.is_dismissed !== true) {
        await refreshBatch(selectedBatchId.value, { quiet: true });
      }
    } else if (reference) {
      // Team-wide Admin sockets receive events for other users. Updating only
      // that batch overview avoids repeatedly downloading every item belonging
      // to the Admin's currently selected batch.
      const updated = applyRealtimeBatchUpdate(event.payload);
      if (!updated && event?.payload?.is_dismissed !== true) {
        await refreshActiveBatchOverviews();
      }
    } else {
      // Older/generic events carry no reference, so fall back to the existing
      // authoritative active-list reconciliation.
      await refreshBatches({ quiet: true });
    }
    lastRefreshAt.value = new Date();
  } catch (_) {
    if (
      connectionState.value !== 'connecting' &&
      socket?.readyState !== WebSocket.OPEN
    ) {
      connectionState.value = 'polling';
    }
  } finally {
    notifyListeners(event);
  }
};

const runRestHeartbeat = () => {
  if (stopped || !store.state.auth.accessToken || pollRefreshPromise) {
    return pollRefreshPromise;
  }
  pollRefreshPromise = Promise.resolve(
    refreshAuthoritativeState({ source: 'poll' })
  ).finally(() => {
    pollRefreshPromise = null;
  });
  return pollRefreshPromise;
};

const flushSocketEvents = async () => {
  socketFlushTimer = null;
  const events = Array.from(pendingSocketEvents.values());
  pendingSocketEvents.clear();
  for (const event of events) {
    if (event.generation !== eventGeneration) continue;
    await refreshAuthoritativeState(event);
  }
};

const clearPendingSocketEvents = () => {
  eventGeneration += 1;
  if (socketFlushTimer) clearTimeout(socketFlushTimer);
  socketFlushTimer = null;
  pendingSocketEvents.clear();
};

const queueSocketEvent = (event) => {
  const reference =
    event?.payload?.batch_reference ||
    event?.payload?.reference ||
    event?.payload?.batch?.reference ||
    '__scope__';
  const previous = pendingSocketEvents.get(String(reference));
  const hasIncomingVersion =
    event?.payload?.version !== undefined && event?.payload?.version !== null;
  const hasPreviousVersion =
    previous?.payload?.version !== undefined &&
    previous?.payload?.version !== null;
  const incomingVersion = Number(event?.payload?.version);
  const previousVersion = Number(previous?.payload?.version);
  if (
    previous &&
    hasIncomingVersion &&
    hasPreviousVersion &&
    Number.isFinite(incomingVersion) &&
    Number.isFinite(previousVersion) &&
    incomingVersion < previousVersion
  ) {
    return;
  }
  pendingSocketEvents.set(String(reference), {
    ...event,
    generation: eventGeneration
  });
  if (!socketFlushTimer) {
    socketFlushTimer = setTimeout(flushSocketEvents, 300);
  }
};

const scheduleReconnect = () => {
  if (stopped || reconnectTimer || !store.state.auth.accessToken) return;
  connectionState.value = 'polling';
  const delay = Math.min(30000, 1000 * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(async () => {
    reconnectTimer = null;
    try {
      // Every reconnect obtains a fresh short-lived signed subprotocol.
      await loadSettings({ quiet: true });
    } finally {
      if (
        !stopped &&
        socket?.readyState !== WebSocket.OPEN &&
        socket?.readyState !== WebSocket.CONNECTING
      ) {
        openSocket();
      }
    }
  }, delay);
};

const openSocket = () => {
  clearConnectionTimers();
  closeSocket();
  const config = socketConfig.value;
  if (stopped) {
    connectionState.value = 'polling';
    return;
  }
  if (!config) {
    connectionState.value = 'polling';
    scheduleReconnect();
    return;
  }

  try {
    connectionState.value = 'connecting';
    const nextSocket = new WebSocket(config.websocketUrl, [config.subprotocol]);
    socket = nextSocket;
    nextSocket.onopen = () => {
      if (socket !== nextSocket) return;
      reconnectAttempts = 0;
      connectionState.value = 'live';
      lastSocketMessageAt = Date.now();
      clearSocketHeartbeat();
      lastSocketMessageAt = Date.now();
      socketHeartbeatTimer = setInterval(() => {
        if (socket !== nextSocket || nextSocket.readyState !== WebSocket.OPEN) {
          return;
        }
        if (Date.now() - lastSocketMessageAt > SOCKET_STALE_MS) {
          nextSocket.close();
          return;
        }
        try {
          nextSocket.send(JSON.stringify({ type: 'ping' }));
        } catch (_) {
          nextSocket.close();
        }
      }, SOCKET_HEARTBEAT_MS);
    };
    nextSocket.onmessage = (message) => {
      if (socket !== nextSocket) return;
      lastSocketMessageAt = Date.now();
      lastEventAt.value = new Date();
      let payload = {};
      try {
        payload = JSON.parse(message.data || '{}');
      } catch (_) {
        payload = {};
      }
      if (payload?.type === 'pong') return;
      queueSocketEvent({ source: 'websocket', payload });
    };
    nextSocket.onerror = () => {
      if (socket !== nextSocket) return;
      connectionState.value = 'polling';
      try {
        nextSocket.close();
      } catch (_) {
        scheduleReconnect();
      }
    };
    nextSocket.onclose = () => {
      if (socket !== nextSocket) return;
      clearSocketHeartbeat();
      socket = null;
      scheduleReconnect();
    };
  } catch (_) {
    scheduleReconnect();
  }
};

const recoverConnection = () => {
  if (stopped || !store.state.auth.accessToken) return Promise.resolve();
  runRestHeartbeat();
  if (
    socket?.readyState === WebSocket.OPEN ||
    socket?.readyState === WebSocket.CONNECTING
  ) {
    return Promise.resolve();
  }
  if (connectionRecoveryPromise) return connectionRecoveryPromise;
  connectionRecoveryPromise = Promise.resolve(loadSettings({ quiet: true }))
    .then(() => {
      if (!stopped) openSocket();
    })
    .finally(() => {
      connectionRecoveryPromise = null;
    });
  return connectionRecoveryPromise;
};

const onOnline = () => recoverConnection();
const onWindowFocus = () => recoverConnection();
const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') recoverConnection();
};

const startRealtime = async () => {
  consumers += 1;
  if (!stopped) return;
  stopped = false;
  window.addEventListener('online', onOnline);
  window.addEventListener('focus', onWindowFocus);
  document.addEventListener('visibilitychange', onVisibilityChange);
  const initialHydration = runRestHeartbeat();
  await loadSettings({ quiet: true });
  if (stopped) return;
  openSocket();
  await initialHydration;
  pollTimer = setInterval(() => {
    pollTicks += 1;
    const needsFrequentRefresh =
      !initialized.value || activeBatches.value.length || listeners.size;
    // Keep a database heartbeat even when initial hydration found no batches.
    // This recovers work started in another tab or missed during a transient
    // authentication/network failure. Active work is always reconciled at the
    // faster interval, even while the WebSocket reports as connected.
    if (!needsFrequentRefresh && pollTicks % 3 !== 0) return;
    runRestHeartbeat();
  }, POLL_INTERVAL_MS);
};

const stopRealtime = () => {
  consumers = Math.max(0, consumers - 1);
  if (consumers) return;
  stopped = true;
  clearConnectionTimers();
  closeSocket();
  window.removeEventListener('online', onOnline);
  window.removeEventListener('focus', onWindowFocus);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  clearPendingSocketEvents();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  pollTicks = 0;
  pollRefreshPromise = null;
  connectionRecoveryPromise = null;
  connectionState.value = 'polling';
};

const subscribeRealtime = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

watch(
  () => [
    store.state.auth.accessToken,
    store.state.auth.currentRole,
    store.state.auth.authUser?.user_id,
    tokenIdentity(store.state.auth.accessToken)
  ],
  async (
    [token, role, userId, credentialIdentity],
    [previousToken, previousRole, previousUserId, previousCredentialIdentity]
  ) => {
    if (stopped) return;
    if (!token) {
      clearConnectionTimers();
      closeSocket();
      clearPendingSocketEvents();
      connectionState.value = 'polling';
      resetState();
      return;
    }
    const roleChanged = Boolean(role && previousRole) && role !== previousRole;
    const tokenChanged = Boolean(previousToken) && token !== previousToken;
    const userChanged =
      Boolean(userId && previousUserId) &&
      String(userId) !== String(previousUserId);
    const credentialIdentityChanged =
      Boolean(credentialIdentity && previousCredentialIdentity) &&
      String(credentialIdentity) !== String(previousCredentialIdentity);
    if (
      !roleChanged &&
      !tokenChanged &&
      !userChanged &&
      !credentialIdentityChanged
    ) {
      return;
    }
    // Authorization can change without a reload. Invalidate any old-scope
    // snapshots, then reconnect with a newly issued short-lived ticket.
    // A normal JWT refresh preserves the in-flight upload; an account/role
    // boundary invalidates all cached rows and pending requests.
    if (roleChanged || userChanged || credentialIdentityChanged) resetState();
    clearConnectionTimers();
    closeSocket();
    clearPendingSocketEvents();
    await loadSettings({ quiet: true });
    if (stopped) return;
    openSocket();
    runRestHeartbeat();
  }
);

const useBulkCvRealtime = () => ({
  connectionState,
  lastEventAt,
  lastRefreshAt,
  startRealtime,
  stopRealtime,
  subscribeRealtime,
  refreshAuthoritativeState
});

export default useBulkCvRealtime;
